/**
 * Shift Roster Grouping
 *
 * Pure helper that decides which overlapping shifts collapse into a single
 * "roster block" versus which render as normal side-by-side blocks.
 *
 * Staggered start times crowd a column just as badly as identical ones, because
 * the side-by-side packer splits it across everything that overlaps.
 */

import type { ShiftListItemDto } from '@signalvote/shared';
import { timeToMinutes, hasUnstaggeredPair } from '@/lib/calendarUtils';

/** Minimum number of simultaneously-running shifts before they collapse into a roster block. */
export const ROSTER_COLLAPSE_THRESHOLD = 3;

/**
 * Fallback duration (minutes) when a shift has no estimatedMinutes. Single
 * source of truth — DayColumn imports this for its own shift-height fallback.
 */
export const DEFAULT_SHIFT_MINUTES = 120;

/** A collapsed cluster of overlapping shifts, spanning earliest start to latest end. */
export interface ShiftRosterGroup {
  /** Stable id, derived from the cluster's earliest start time (e.g. "roster:18:00"). */
  id: string;
  startMinutes: number;
  endMinutes: number;
  shifts: ShiftListItemDto[];
}

/**
 * Partition a day's shifts into collapsed roster groups and un-collapsed singles.
 *
 * Shifts are clustered by overlap, directly or through a chain. A cluster
 * collapses when `threshold` of them run *simultaneously* (a chain of pairwise
 * overlaps like 9-11, 10-12, 11:30-13:30 never crowds the column past two
 * blocks, so it stays as singles), or when any overlapping pair in it starts
 * too close together to lay out side by side. Shifts with no `startTime` can't
 * be positioned, so they pass through to `singles` untouched.
 */
export function partitionShiftsIntoRosterGroups(
  shifts: ShiftListItemDto[],
  threshold: number = ROSTER_COLLAPSE_THRESHOLD
): { groups: ShiftRosterGroup[]; singles: ShiftListItemDto[] } {
  const singles: ShiftListItemDto[] = [];
  const spans: { shift: ShiftListItemDto; start: number; end: number }[] = [];

  for (const shift of shifts) {
    if (!shift.startTime) {
      // No start time — can't position or group; keep as a single, untouched.
      singles.push(shift);
      continue;
    }
    const start = timeToMinutes(shift.startTime);
    spans.push({
      shift,
      start,
      end: start + (shift.estimatedMinutes || DEFAULT_SHIFT_MINUTES),
    });
  }

  // Walk in start order, opening a new cluster whenever a shift starts at or
  // after everything before it has ended. Starting exactly when the cluster ends
  // is a handoff, not an overlap.
  spans.sort((a, b) => a.start - b.start || a.end - b.end);
  const clusters: (typeof spans)[] = [];
  let clusterEnd = -Infinity;
  for (const span of spans) {
    if (span.start >= clusterEnd) {
      clusters.push([span]);
      clusterEnd = span.end;
    } else {
      clusters[clusters.length - 1].push(span);
      clusterEnd = Math.max(clusterEnd, span.end);
    }
  }

  const groups: ShiftRosterGroup[] = [];
  for (const cluster of clusters) {
    // A pair with no stagger is under the concurrency threshold but still has no
    // readable side-by-side layout, so the roster is what's left for it.
    const spans = cluster.map((s) => ({ startMinutes: s.start, endMinutes: s.end }));
    if (peakConcurrency(cluster) >= threshold || hasUnstaggeredPair(spans)) {
      groups.push({
        id: `roster:${cluster[0].shift.startTime}`,
        startMinutes: cluster[0].start,
        endMinutes: Math.max(...cluster.map((s) => s.end)),
        shifts: cluster.map((s) => s.shift),
      });
    } else {
      // Below threshold — these render as normal side-by-side blocks.
      singles.push(...cluster.map((s) => s.shift));
    }
  }

  return { groups, singles };
}

/**
 * The most shifts running at any one moment. Peak concurrency always happens at
 * some shift's start, so counting what is live at each start covers every case.
 *
 * ponytail: O(n^2) over one day's shifts, which is tens at most. Swap in a
 * start/end sweep if a single day ever holds thousands.
 */
function peakConcurrency(cluster: { start: number; end: number }[]): number {
  return Math.max(
    ...cluster.map(
      (a) => cluster.filter((b) => b.start <= a.start && a.start < b.end).length
    )
  );
}
